import React, { useEffect, useRef, ReactNode } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Browser from '@ww-digital/web-palette-react/dist/components/Utility/Browser';

interface ScrollRestorationProps {
  children: ReactNode;
}

interface JumpLink {
  id: string;
  method: 'scroll' | 'hash' | '';
  element?: HTMLElement | null;
}

const MAX_SCROLL_ATTEMPTS = 3;
const SCROLL_DURATION = 2500;
const INITIAL_RENDER_DELAY = 100;
const VERIFICATION_DELAY = 500;
const ELEMENT_SEARCH_TIMEOUT = 5000;
const HASH_OFFSET = '70';
const POSITION_THRESHOLD = 10;

export const ScrollRestoration = ({
  children,
}: ScrollRestorationProps): JSX.Element => {
  const location = useLocation();
  const history = useHistory();

  const { hash } = location;

  const timerHandle = useRef<null | number | ReturnType<typeof setTimeout>>(
    null,
  );
  // Single verification timeout ref - only one verification (hash or scroll) can occur at a time
  const verificationTimeout = useRef<
    null | number | ReturnType<typeof setTimeout>
  >(null);
  const scrollAttempts = useRef(0);
  const userHasInteracted = useRef(false);
  const handlersAdded = useRef(false);

  const SCROLL_EVENTS = [
    'wheel',
    'touchmove',
    'keydown',
    'touchstart',
    'mousedown',
  ] as const;

  const handleUserInteraction = () => {
    // Any user interaction should disable auto-scrolling
    userHasInteracted.current = true;
    console.log('[ScrollRestoration] User interaction detected');

    removeScrollHandlers();
  };

  const setupScrollHandlers = () => {
    if (handlersAdded.current) {
      return; // Don't add handlers if they're already added
    }

    SCROLL_EVENTS.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, {
        passive: true,
      });
    });
    handlersAdded.current = true;
  };

  const removeScrollHandlers = () => {
    if (!handlersAdded.current) {
      return; // Don't try to remove if they're not added
    }

    SCROLL_EVENTS.forEach((event) => {
      document.removeEventListener(event, handleUserInteraction);
    });
    handlersAdded.current = false;
  };

  // Reset scroll state when location changes (for SPA navigation)
  useEffect(() => {
    // Clear any setTimeout Running
    clearTimer();
    scrollAttempts.current = 0;
    userHasInteracted.current = false;

    // Re-add handlers for the new navigation
    setupScrollHandlers();

    // Handle navigation to hash links
    if (Browser.isBrowser()) {
      if (hash) {
        handleJumpLink(hash);
      } else {
        window.scrollTo(0, 0);
      }
    }

    // Cleanup
    return () => {
      clearTimer();
      removeScrollHandlers();
    };
  }, [location, hash]);

  // Initial setup of handlers
  useEffect(() => {
    setupScrollHandlers();
    return () => {
      removeScrollHandlers();
    };
  }, []);

  async function setTimer() {
    if (typeof timerHandle === 'number') {
      return;
    }

    timerHandle.current = setTimeout(() => {
      timerHandle.current = 0;
    }, ELEMENT_SEARCH_TIMEOUT);
  }

  async function clearTimer() {
    // Check if Timer is Running
    if (timerHandle.current) {
      // Clear Timer
      clearTimeout(timerHandle.current);
      timerHandle.current = null;
    }
    // Clear verification timeout if it exists
    if (verificationTimeout.current) {
      clearTimeout(verificationTimeout.current);
      verificationTimeout.current = null;
    }
  }

  // Helper to safely set verification timeout
  const setVerificationTimeout = (callback: () => void) => {
    // Clear any existing verification timeout first
    if (verificationTimeout.current) {
      clearTimeout(verificationTimeout.current);
      verificationTimeout.current = null;
    }
    verificationTimeout.current = setTimeout(() => {
      callback();
      verificationTimeout.current = null;
    }, VERIFICATION_DELAY);
  };

  // Common verification logic for both hash and scroll methods
  const verifyScrollPosition = (
    element: HTMLElement | null | undefined,
    hash: string,
    duration: number,
  ) => {
    if (userHasInteracted.current) {
      return;
    }

    const currentY = element?.getBoundingClientRect().y ?? 0;
    if (Math.abs(currentY) > POSITION_THRESHOLD) {
      scrollAttempts.current++;
      handleJumpLink(hash, duration);
    }
  };

  async function handleJumpLink(hash: string, duration = SCROLL_DURATION) {
    if (
      scrollAttempts.current >= MAX_SCROLL_ATTEMPTS ||
      userHasInteracted.current
    ) {
      return;
    }

    // Get jumplink/ID from URL
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const jumpLink: JumpLink = { id: '', method: '' };

    if (hash.indexOf('/scrollto/') === 1) {
      jumpLink.method = 'scroll';
      jumpLink.id = hash.split('/scrollto/')[1];
    } else {
      jumpLink.method = 'hash';
      jumpLink.id = hash.split('#')[1];
    }

    try {
      // Wait for Element to Appear
      await new Promise((resolve, reject) => {
        setTimer();

        const getElement = () => {
          jumpLink.element = document.getElementById(jumpLink.id);
          if (jumpLink.element) {
            clearTimer();
            resolve(jumpLink.element);
          } else {
            if (timerHandle) {
              window.requestAnimationFrame(getElement);
            } else {
              reject();
            }
          }
        };
        getElement();
      });

      if (jumpLink.element) {
        // Implement logic for either hash or scrollTo element
        const stickyClass = '[class*="NavigationSlice_sticky"]';
        const stickyNav = document.querySelector(stickyClass) as HTMLElement;
        const hashOffset = jumpLink.element.getAttribute('data-hash-offset');

        // Add a small delay to ensure the page is fully rendered
        await new Promise((resolve) =>
          setTimeout(resolve, INITIAL_RENDER_DELAY),
        );

        if (userHasInteracted.current) {
          return;
        }

        if (jumpLink.method === 'hash' && stickyNav) {
          jumpLink.element.setAttribute('data-hash-offset', HASH_OFFSET);
          jumpLink.element.style.paddingTop = `${stickyNav.offsetHeight}px`;
          jumpLink.element.style.marginTop = `-${stickyNav.offsetHeight}px`;

          // Handle Scroll to Element
          const elementY = jumpLink.element.getBoundingClientRect().y;
          const startingY = window.pageYOffset;
          const finalY = startingY + elementY;

          console.log(
            `[ScrollRestoration] Hash scroll (${scrollAttempts.current + 1})`,
          );
          window.scrollTo(0, finalY);

          // Add a verification scroll after a short delay
          setVerificationTimeout(() => {
            verifyScrollPosition(jumpLink.element, hash, duration);
          });
        } else if (jumpLink.method === 'scroll') {
          // Delay slightly to let page format (esp. CardGrid).
          await new Promise((resolve) =>
            setTimeout(resolve, INITIAL_RENDER_DELAY),
          );

          if (userHasInteracted.current) {
            return;
          }

          let elementY = jumpLink.element.getBoundingClientRect().y;
          elementY =
            hashOffset || !stickyNav
              ? elementY
              : elementY - stickyNav.offsetHeight;
          const startingY = window.pageYOffset;
          const easing = (t: number) => {
            return t < 0.5
              ? 4 * t * t * t
              : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
          };

          let start: number;

          window.requestAnimationFrame(function step(timestamp) {
            if (userHasInteracted.current) {
              return;
            }

            if (!start) {
              start = timestamp;
              const finalY = startingY + elementY;
              console.log(
                `[ScrollRestoration] Hash scroll (${scrollAttempts.current + 1})`,
              );
            }
            const time = timestamp - start;
            let percent = Math.min(time / duration, 1);
            percent = easing(percent);
            window.scrollTo(0, startingY + elementY * percent);

            if (time < duration) {
              window.requestAnimationFrame(step);
            } else {
              // Verify final scroll position
              setVerificationTimeout(() => {
                verifyScrollPosition(jumpLink.element, hash, duration);
              });
            }
          });
        }
      }
    } catch (error) {
      console.warn('Failed to handle jump link:', error);
    }
  }

  return <>{children}</>;
};
